import { useCallback } from "react";
import { Grid, Box, Group, Text, Button, Divider, Modal } from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconBuildingHospital, IconPlus } from "@tabler/icons-react";
import {
  useAccounts,
  useSurgeryCenters,
  usePhysicians,
} from "../../hooks/useReferenceData";
import { useCreatePractice } from "../../hooks/admin/usePractices";
import { useCreatePhysician } from "../../hooks/admin/usePhysicians";
import { useCreateSurgeryCenterLab } from "../../hooks/admin/useSurgeryCentersLabs";
import PracticeForm, {
  PracticeFormValues,
} from "../admin/practices/PracticeForm";
import PhysicianForm, {
  PhysicianFormValues,
} from "../admin/physicians/PhysicianForm";
import SurgeryCenterLabForm, {
  SurgeryCenterLabFormValues,
} from "../admin/surgeryCentersLabs/SurgeryCenterLabForm";
import { CertaintySelect } from "./CertaintyInput";
import SmartSuggestion from "./SmartSuggestion";

const sanitizeOptions = <T extends { value: string | null | undefined }>(
  opts: T[] | undefined
): T[] => {
  if (!Array.isArray(opts)) return [];
  return opts.filter(
    (o) => typeof o.value !== "undefined" && o.value !== null && o.value !== ""
  );
};

interface OriginatingFacilityFormProps {
  form: UseFormReturnType<any>;
  certainties?: Record<string, number>;
  onCertaintyChange?: (field: string, certainty: number) => void;
  unmatchedExtractions?: Record<string, string>;
  onClearUnmatched?: (field: string) => void;
}

export default function OriginatingFacilityForm({
  form,
  certainties = {},
  onCertaintyChange,
  unmatchedExtractions = {},
  onClearUnmatched,
}: OriginatingFacilityFormProps) {
  const { data: practices = [] } = useAccounts();
  const { data: surgeryCenters = [] } = useSurgeryCenters();
  const { data: physicians = [] } = usePhysicians();

  const createPractice = useCreatePractice();
  const createPhysician = useCreatePhysician();
  const createSurgeryCenter = useCreateSurgeryCenterLab();

  const [
    practiceModalOpened,
    { open: openPracticeModal, close: closePracticeModal },
  ] = useDisclosure(false);
  const [
    physicianModalOpened,
    { open: openPhysicianModal, close: closePhysicianModal },
  ] = useDisclosure(false);
  const [
    surgeryCenterModalOpened,
    { open: openSurgeryCenterModal, close: closeSurgeryCenterModal },
  ] = useDisclosure(false);

  const isSurgeryCenterNameLinked = useCallback(
    (name: string) =>
      surgeryCenters.some(
        (sc) =>
          sc.label.split(" (")[0].trim().toLowerCase() ===
          name.trim().toLowerCase()
      ),
    [surgeryCenters]
  );

  const handlePracticeSubmit = useCallback(
    async (values: PracticeFormValues) => {
      const practice = await createPractice.mutateAsync({
        name: values.name,
        shortName: values.shortName || undefined,
        primaryPhone: values.primaryPhone || undefined,
        secondaryPhone: values.secondaryPhone || undefined,
        fax: values.fax || undefined,
        preferredPathologistId: values.preferredPathologistId || undefined,
      });
      form.setFieldValue("case.originatingPracticeId", practice.id);
      closePracticeModal();
    },
    [createPractice, form, closePracticeModal]
  );

  const handlePhysicianSubmit = useCallback(
    async (values: PhysicianFormValues) => {
      const physician = await createPhysician.mutateAsync(values);
      form.setFieldValue("case.originatingPhysicianId", physician.id);
      closePhysicianModal();
    },
    [createPhysician, form, closePhysicianModal]
  );

  const handleSurgeryCenterSubmit = useCallback(
    async (values: SurgeryCenterLabFormValues) => {
      if (isSurgeryCenterNameLinked(values.name)) {
        notifications.show({
          color: "red",
          title: "Duplicate",
          message: `"${values.name}" already exists as a surgical center.`,
        });
        return;
      }
      const surgeryCenter = await createSurgeryCenter.mutateAsync({
        name: values.name,
        type: values.type,
        address: values.address || null,
        primaryPhone: values.primaryPhone || null,
        secondaryPhone: values.secondaryPhone || null,
        fax: values.fax || null,
        externalCaseNumberPrefix: values.externalCaseNumberPrefix || null,
        preferredPathologistId: values.preferredPathologistId || null,
        logoS3Key: values.logoS3Key || null,
      });
      form.setFieldValue("case.originatingSurgeryCenterId", surgeryCenter.id);
      closeSurgeryCenterModal();
    },
    [
      createSurgeryCenter,
      form,
      isSurgeryCenterNameLinked,
      closeSurgeryCenterModal,
    ]
  );

  const handleAddPracticeSuggestion = useCallback(async () => {
    const practice = await createPractice.mutateAsync({
      name: unmatchedExtractions["case.originatingPracticeId"],
    });
    form.setFieldValue("case.originatingPracticeId", practice.id);
    onClearUnmatched?.("case.originatingPracticeId");
  }, [createPractice, form, onClearUnmatched, unmatchedExtractions]);

  const handleAddPhysicianSuggestion = useCallback(async () => {
    const name = unmatchedExtractions["case.originatingPhysicianId"];
    const parts = name.split(" ");
    const firstName = parts[0] || name;
    const lastName = parts.slice(1).join(" ") || "";
    const physician = await createPhysician.mutateAsync({
      firstName,
      lastName,
    });
    form.setFieldValue("case.originatingPhysicianId", physician.id);
    onClearUnmatched?.("case.originatingPhysicianId");
  }, [createPhysician, form, onClearUnmatched, unmatchedExtractions]);

  const handleAddSurgeryCenterSuggestion = useCallback(async () => {
    const sc = await createSurgeryCenter.mutateAsync({
      name: unmatchedExtractions["case.originatingSurgeryCenterId"],
      type: "surgery_center",
    });
    form.setFieldValue("case.originatingSurgeryCenterId", sc.id);
    onClearUnmatched?.("case.originatingSurgeryCenterId");
  }, [createSurgeryCenter, form, onClearUnmatched, unmatchedExtractions]);

  return (
    <>
      <Grid gutter="md">
        <Grid.Col span={12}>
          <Divider
            label={
              <Group gap="xs">
                <IconBuildingHospital size={14} />
                <Text size="sm" fw={500}>
                  Originating Facility Information
                </Text>
              </Group>
            }
            labelPosition="left"
          />
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Box>
            <CertaintySelect
              label="Originating Practice"
              placeholder="Select originating practice"
              description="The practice that sent the case"
              name="case.originatingPracticeId"
              data={sanitizeOptions(practices)}
              searchable
              {...form.getInputProps("case.originatingPracticeId")}
              certainty={certainties["case.originatingPracticeId"]}
              onCertaintyChange={(c) =>
                onCertaintyChange?.("case.originatingPracticeId", c)
              }
            />
            {unmatchedExtractions["case.originatingPracticeId"] &&
            !form.values.case?.originatingPracticeId ? (
              <SmartSuggestion
                extractedValue={
                  unmatchedExtractions["case.originatingPracticeId"]
                }
                onAdd={handleAddPracticeSuggestion}
                isLoading={createPractice.isPending}
              />
            ) : (
              <Group justify="flex-end" mt={4}>
                <Button
                  variant="subtle"
                  size="compact-xs"
                  leftSection={<IconPlus size={12} />}
                  onClick={openPracticeModal}
                >
                  Add New
                </Button>
              </Group>
            )}
          </Box>
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Box>
            <CertaintySelect
              label="Originating Physician"
              placeholder="Select originating physician"
              description="The physician from the originating facility"
              name="case.originatingPhysicianId"
              data={sanitizeOptions(physicians)}
              searchable
              {...form.getInputProps("case.originatingPhysicianId")}
              certainty={certainties["case.originatingPhysicianId"]}
              onCertaintyChange={(c) =>
                onCertaintyChange?.("case.originatingPhysicianId", c)
              }
            />
            {unmatchedExtractions["case.originatingPhysicianId"] &&
            !form.values.case?.originatingPhysicianId ? (
              <SmartSuggestion
                extractedValue={
                  unmatchedExtractions["case.originatingPhysicianId"]
                }
                onAdd={handleAddPhysicianSuggestion}
                isLoading={createPhysician.isPending}
              />
            ) : (
              <Group justify="flex-end" mt={4}>
                <Button
                  variant="subtle"
                  size="compact-xs"
                  leftSection={<IconPlus size={12} />}
                  onClick={openPhysicianModal}
                >
                  Add New
                </Button>
              </Group>
            )}
          </Box>
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Box>
            <CertaintySelect
              label="Originating Surgery Center"
              placeholder="Select originating surgery center"
              description="The surgery center from the originating facility"
              name="case.originatingSurgeryCenterId"
              data={sanitizeOptions(surgeryCenters)}
              searchable
              {...form.getInputProps("case.originatingSurgeryCenterId")}
              certainty={certainties["case.originatingSurgeryCenterId"]}
              onCertaintyChange={(c) =>
                onCertaintyChange?.("case.originatingSurgeryCenterId", c)
              }
            />
            {unmatchedExtractions["case.originatingSurgeryCenterId"] &&
            !form.values.case?.originatingSurgeryCenterId ? (
              <SmartSuggestion
                extractedValue={
                  unmatchedExtractions["case.originatingSurgeryCenterId"]
                }
                onAdd={handleAddSurgeryCenterSuggestion}
                isLoading={createSurgeryCenter.isPending}
              />
            ) : (
              <Group justify="flex-end" mt={4}>
                <Button
                  variant="subtle"
                  size="compact-xs"
                  leftSection={<IconPlus size={12} />}
                  onClick={openSurgeryCenterModal}
                >
                  Add New
                </Button>
              </Group>
            )}
          </Box>
        </Grid.Col>
      </Grid>

      {/* Quick-Add Practice Modal */}
      <Modal.Root
        opened={practiceModalOpened}
        onClose={closePracticeModal}
        size="md"
        closeOnClickOutside={false}
        styles={{
          content: {
            display: "flex",
            flexDirection: "column",
            maxHeight: "85vh",
          },
          header: { flexShrink: 0 },
          body: { overflowY: "auto", flex: 1 },
        }}
      >
        <Modal.Overlay />
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>Add New Originating Practice</Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>
          <Modal.Body>
            <PracticeForm
              formId="practice-form-originating"
              hideFooter
              onSubmit={handlePracticeSubmit}
            />
          </Modal.Body>
          <Box
            px="md"
            py="sm"
            style={{
              borderTop: "1px solid var(--mantine-color-default-border)",
              flexShrink: 0,
            }}
          >
            <Group justify="space-between">
              <Button
                variant="default"
                onClick={closePracticeModal}
                type="button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="practice-form-originating"
                loading={createPractice.isPending}
              >
                Create
              </Button>
            </Group>
          </Box>
        </Modal.Content>
      </Modal.Root>

      {/* Quick-Add Physician Modal */}
      <Modal.Root
        opened={physicianModalOpened}
        onClose={closePhysicianModal}
        size="md"
        closeOnClickOutside={false}
        styles={{
          content: {
            display: "flex",
            flexDirection: "column",
            maxHeight: "85vh",
          },
        }}
      >
        <Modal.Overlay />
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>Add New Originating Physician</Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>
          <Modal.Body style={{ flex: 1, overflow: "auto" }}>
            <PhysicianForm
              formId="physician-form-originating"
              onSubmit={handlePhysicianSubmit}
            />
          </Modal.Body>
          <Divider />
          <Group justify="space-between" p="md">
            <Button variant="default" onClick={closePhysicianModal}>
              Cancel
            </Button>
            <Button
              type="submit"
              form="physician-form-originating"
              loading={createPhysician.isPending}
            >
              Create
            </Button>
          </Group>
        </Modal.Content>
      </Modal.Root>

      {/* Quick-Add Surgery Center Modal */}
      <Modal.Root
        opened={surgeryCenterModalOpened}
        onClose={closeSurgeryCenterModal}
        size="xl"
        closeOnClickOutside={false}
        styles={{
          content: {
            display: "flex",
            flexDirection: "column",
            maxHeight: "85vh",
          },
        }}
      >
        <Modal.Overlay />
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>Add New Originating Surgery Center</Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>
          <Modal.Body style={{ flex: 1, overflow: "auto" }}>
            <SurgeryCenterLabForm
              formId="surgery-center-form-originating"
              hideFooter
              onSubmit={handleSurgeryCenterSubmit}
              defaultType="surgery_center"
            />
          </Modal.Body>
          <Divider />
          <Group justify="space-between" p="md">
            <Button
              variant="default"
              onClick={closeSurgeryCenterModal}
              type="button"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="surgery-center-form-originating"
              loading={createSurgeryCenter.isPending}
            >
              Create
            </Button>
          </Group>
        </Modal.Content>
      </Modal.Root>
    </>
  );
}
